import { DatasetListItem } from "datasets/types/interfaces/datasetListItem";
import { PanelRightClose } from "lucide-react";
import { Button } from "~/components/ui/button";
import { Tooltip, TooltipContent, TooltipTrigger } from "~/components/ui/tooltip";
import {
  MCP_APP_GROUP_LABELS,
  useMcpAppsPaneState,
} from "~/pages/manifold-nextgen/components/conversations/useMcpAppsPane";
import { useSessionEvalRuns } from "~/pages/manifold-nextgen/components/conversations/useSessionEvalRuns";
import { useSessionIngests } from "~/pages/manifold-nextgen/components/conversations/useSessionIngests";
import { useSessionPlan } from "~/pages/manifold-nextgen/components/conversations/useSessionPlan";
import {
  decodePublicDatasetRunKey,
  encodePublicDatasetRunKey,
  useSessionPublicDatasetRuns,
} from "~/pages/manifold-nextgen/components/conversations/useSessionPublicDatasetRuns";
import {
  PanelPosition,
  useResizablePanelWidth,
} from "~/pages/manifold-nextgen/hooks/useResizablePanelWidth";
import {
  SessionContextPanelType,
  useSessionContextPanel,
} from "~/pages/manifold-nextgen/hooks/useSessionContextPanel";
import { Agent } from "~/services/agents";
import { SessionContextAgentDetail } from "./SessionContextAgentDetail";
import { SessionContextAppsDetail } from "./SessionContextAppsDetail";
import { SessionContextDatasetDetail } from "./SessionContextDatasetDetail";
import { SessionContextEvalRunDetail } from "./SessionContextEvalRunDetail";
import { SessionContextEvalRunList } from "./SessionContextEvalRunList";
import { SessionContextIngestDetail } from "./SessionContextIngestDetail";
import { SessionContextIngestList } from "./SessionContextIngestList";
import { SessionContextPlanDetail } from "./SessionContextPlanDetail";
import { SessionContextPublicDatasetRunDetail } from "./SessionContextPublicDatasetRunDetail";
import { SessionContextPublicDatasetRunList } from "./SessionContextPublicDatasetRunList";
import { SessionContextToc } from "./SessionContextToc";

const MIN_WIDTH = 360;
const MAX_WIDTH = 840;
const DEFAULT_WIDTH = 420;

interface SessionContextSidebarProps {
  projectId: string;
  isOwner: boolean;
  attachedDatasets: DatasetListItem[] | undefined;
  attachedAgents: Agent[] | undefined;
  isCollapsed: boolean;
  onCollapse: () => void;
}

export const SessionContextSidebar = ({
  projectId,
  isOwner,
  attachedDatasets,
  attachedAgents,
  isCollapsed,
  onCollapse,
}: SessionContextSidebarProps) => {
  const { panel, openPanel, setView, goHome } = useSessionContextPanel();
  const appsPane = useMcpAppsPaneState();
  const ingests = useSessionIngests();
  const evalRuns = useSessionEvalRuns();
  const publicDatasetRuns = useSessionPublicDatasetRuns();
  const { plan, isRunning: isAgentRunning } = useSessionPlan();
  const { width, handleDragStart, contentRef } = useResizablePanelWidth({
    minWidth: MIN_WIDTH,
    maxWidth: MAX_WIDTH,
    initialWidth: DEFAULT_WIDTH,
    position: PanelPosition.RIGHT,
  });

  // Opening the group's TOC row shows whichever of its tabs is already active,
  // or falls back to the most recently opened one for that server.
  const handleOpenApp = (serverId: string) => {
    const groupTabs = appsPane.tabs.filter((tab) => tab.serverId === serverId);
    const alreadyActive = groupTabs.some((tab) => tab.key === appsPane.activeKey);
    if (!alreadyActive) {
      const fallback = groupTabs[groupTabs.length - 1];
      if (fallback) {
        appsPane.setActive(fallback.key);
      }
    }
    openPanel(SessionContextPanelType.APPS, serverId);
  };

  return (
    <div
      role="region"
      aria-label="Session context"
      aria-hidden={isCollapsed}
      inert={isCollapsed}
      className="relative flex shrink-0 justify-end overflow-hidden bg-background transition-[width] duration-300 ease-in-out"
      style={{ width: isCollapsed ? 0 : width }}
    >
      <div
        onMouseDown={handleDragStart}
        className="group absolute inset-y-0 left-0 z-20 w-1.5 cursor-col-resize"
      >
        <div className="pointer-events-none absolute inset-y-0 left-0 w-px bg-border transition-all group-hover:w-0.5 group-hover:bg-primary group-active:w-0.5 group-active:bg-primary" />
      </div>

      {/* Fixed to the panel's un-collapsed width so text reflows only when the
          user resizes it — the outer region's width is what animates on
          collapse, clipping this via overflow-hidden instead of squeezing it. */}
      <div
        ref={contentRef}
        className="flex h-full shrink-0 flex-col overflow-hidden"
        style={{ width }}
      >
        {!panel ? (
          <>
            <div className="flex h-14 shrink-0 items-center gap-1 border-b border-border pl-4 pr-2">
              <span className="text-sm font-medium text-foreground">Context</span>
              <Tooltip delayDuration={700}>
                <TooltipTrigger asChild>
                  <Button
                    variant="ghost"
                    size="icon"
                    onClick={onCollapse}
                    aria-label="Collapse session context"
                    className="ml-auto size-8"
                  >
                    <PanelRightClose className="size-4" />
                  </Button>
                </TooltipTrigger>
                <TooltipContent side="bottom">Toggle Context</TooltipContent>
              </Tooltip>
            </div>
            <div className="flex-1 overflow-y-auto">
              <SessionContextToc
                projectId={projectId}
                isOwner={isOwner}
                attachedDatasets={attachedDatasets}
                attachedAgents={attachedAgents}
                onOpenDataset={(datasetId) => openPanel(SessionContextPanelType.DATASET, datasetId)}
                apps={appsPane.tabs}
                onOpenAgent={(agentId) => openPanel(SessionContextPanelType.AGENT, agentId)}
                onOpenApp={handleOpenApp}
                ingests={ingests}
                onOpenIngests={() => openPanel(SessionContextPanelType.INGEST_LIST)}
                evalRuns={evalRuns}
                onOpenEvalRuns={() => openPanel(SessionContextPanelType.EVAL_RUN_LIST)}
                publicDatasetRuns={publicDatasetRuns}
                onOpenPublicDatasetRuns={() =>
                  openPanel(SessionContextPanelType.PUBLIC_DATASET_RUN_LIST)
                }
                plan={plan}
                onOpenPlan={() => openPanel(SessionContextPanelType.PLAN)}
              />
            </div>
          </>
        ) : panel.type === SessionContextPanelType.AGENT ? (
          <SessionContextAgentDetail
            agentId={panel.id}
            view={panel.view}
            onBack={goHome}
            onCollapse={onCollapse}
            onSetView={setView}
          />
        ) : panel.type === SessionContextPanelType.DATASET ? (
          <SessionContextDatasetDetail
            datasetId={panel.id}
            view={panel.view}
            onBack={goHome}
            onCollapse={onCollapse}
            onSetView={setView}
          />
        ) : panel.type === SessionContextPanelType.APPS ? (
          <SessionContextAppsDetail
            groupLabel={MCP_APP_GROUP_LABELS[panel.id] ?? panel.id}
            onBack={goHome}
            onCollapse={onCollapse}
          />
        ) : panel.type === SessionContextPanelType.INGEST_LIST ? (
          <SessionContextIngestList
            ingests={ingests}
            onOpenIngest={(jobId) => openPanel(SessionContextPanelType.INGEST, jobId)}
            onBack={goHome}
            onCollapse={onCollapse}
          />
        ) : panel.type === SessionContextPanelType.INGEST ? (
          <SessionContextIngestDetail
            jobId={panel.id}
            ingests={ingests}
            onSelect={(jobId) => openPanel(SessionContextPanelType.INGEST, jobId)}
            onBack={() => openPanel(SessionContextPanelType.INGEST_LIST)}
            onCollapse={onCollapse}
          />
        ) : panel.type === SessionContextPanelType.EVAL_RUN_LIST ? (
          <SessionContextEvalRunList
            evalRuns={evalRuns}
            onOpenEvalRun={(runId) => openPanel(SessionContextPanelType.EVAL_RUN, runId)}
            onBack={goHome}
            onCollapse={onCollapse}
          />
        ) : panel.type === SessionContextPanelType.EVAL_RUN ? (
          <SessionContextEvalRunDetail
            runId={panel.id}
            evalRuns={evalRuns}
            onSelect={(runId) => openPanel(SessionContextPanelType.EVAL_RUN, runId)}
            onBack={() => openPanel(SessionContextPanelType.EVAL_RUN_LIST)}
            onCollapse={onCollapse}
          />
        ) : panel.type === SessionContextPanelType.PUBLIC_DATASET_RUN_LIST ? (
          <SessionContextPublicDatasetRunList
            runs={publicDatasetRuns}
            onOpenRun={(dagId, runId) =>
              openPanel(
                SessionContextPanelType.PUBLIC_DATASET_RUN,
                encodePublicDatasetRunKey(dagId, runId)
              )
            }
            onBack={goHome}
            onCollapse={onCollapse}
          />
        ) : panel.type === SessionContextPanelType.PUBLIC_DATASET_RUN ? (
          <SessionContextPublicDatasetRunDetail
            {...decodePublicDatasetRunKey(panel.id)}
            runs={publicDatasetRuns}
            onSelect={(dagId, runId) =>
              openPanel(
                SessionContextPanelType.PUBLIC_DATASET_RUN,
                encodePublicDatasetRunKey(dagId, runId)
              )
            }
            onBack={() => openPanel(SessionContextPanelType.PUBLIC_DATASET_RUN_LIST)}
            onCollapse={onCollapse}
          />
        ) : panel.type === SessionContextPanelType.PLAN ? (
          <SessionContextPlanDetail
            plan={plan}
            isActive={isAgentRunning}
            onBack={goHome}
            onCollapse={onCollapse}
          />
        ) : null}
      </div>
    </div>
  );
};
